/* eslint-disable max-len */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { CrudType } from '../../../utility/types/CrudType';
import { CrudStatus } from '../../../utility/types/CrudStatus';
import envConfig from '../../../envConfig';
import RaaAuthenticatedRemoteService from '../../../utility/RaaAuthenticatedRemoteService';

export const getHiddenAdapterSensors = (hiddenChannelGroups, adapterSensors) => hiddenChannelGroups
  .filter((hiddenKey) => adapterSensors.some((sensor) => sensor.channelGroups.some((group) => group.channelKey === hiddenKey)));

export const getHiddenRegularSensors = (hiddenChannelGroups, adapterSensors) => hiddenChannelGroups
  .filter((hiddenKey) => !adapterSensors.some((sensor) => sensor.channelGroups.some((group) => group.channelKey === hiddenKey)));

// Selectors
const selectSingleDevice = (state) => state.singleDevice.device;
const selectSingleDeviceCrudOperation = (state) => state.singleDevice.crudOperation;
const selectSingleDeviceCrudStatus = (state) => state.singleDevice.crudStatus;
const selectSingleDeviceErrors = (state) => state.singleDevice.errors;
const selectSingleDeviceInitialLoading = (state) => state.singleDevice.initialLoad;
const selectDeviceAlertChannels = (state) => state.singleDevice.alertChannels;
const selectAlertChannelsStatus = (state) => state.singleDevice.alertChannelsStatus;
const selectPublicLinkStatus = (state) => state.singleDevice.publicLinkStatus;
const selectDevicePublicPageHash = (state) => state.singleDevice.publicLinkUrl;
const selectSensorUpdateStatus = (state) => state.singleDevice.sensorUpdateStatus;
const selectLocationUpdateStatus = (state) => state.singleDevice.locationUpdateStatus;
const selectHiddenChannelGroups = (state) => state.singleDevice.hiddenChannelGroups;

// Async Thunks
const fetchSingleDevice = createAsyncThunk(
  'securedApi/singleDevice/fetch',
  async ({ deviceId = null, includeArchived = false } = {}, thunkAPI) => {
    try {
      const state = thunkAPI.getState().singleDevice;
      let effectiveDeviceId = null;
      if (!deviceId) {
        if (state && state.device) {
          effectiveDeviceId = state.device.general && state.device.general.id;
        } else {
          throw new Error('No device ID provided.');
        }
      } else {
        effectiveDeviceId = deviceId;
      }
      if (!effectiveDeviceId) {
        return thunkAPI.rejectWithValue({
          message: 'No device ID provided.',
        });
      }
      const params = includeArchived ? '?includeArchived=true' : '';
      return await RaaAuthenticatedRemoteService.globalInstance()
        .get(`${envConfig.apiUrl}/api/v1/device-data/device-details/${effectiveDeviceId}${params}`);
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message,
      });
    }
  },
);

const updateDeviceName = createAsyncThunk(
  'securedApi/singleDevice/updateName',
  async ({ deviceId, name }, thunkAPI) => {
    try {
      const response = await RaaAuthenticatedRemoteService.globalInstance().patch(
        `${envConfig.apiUrl}/api/v1/device-data/update-device/${deviceId}`, { data: { name } },
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message,
      });
    }
  },
);

const updateSensor = createAsyncThunk(
  'securedApi/singleDevice/updateSensor',
  async (payload, thunkAPI) => {
    try {
      const response = await RaaAuthenticatedRemoteService.globalInstance().patch(
        `${envConfig.apiUrl}/api/v1/device-data/update-sensor/`,
        {
          data: {
            channelKey: payload.channelKey,
            channels: payload.channels,
            label: payload.label,
            isHidden: payload.isHidden
              ? payload.isHidden : undefined,
          },
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
      return response.channelGroup;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.data);
    }
  },
);

const createDevicePublicLink = createAsyncThunk(
  'securedApi/singleDevice/createPublicLink',
  async ({ deviceId }, thunkAPI) => {
    try {
      const response = await RaaAuthenticatedRemoteService.globalInstance()
        .post(`${envConfig.apiUrl}/api/v1/device-data/public-link/${deviceId}`);
      return response;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message,
      });
    }
  },
);

const deleteDevicePublicLink = createAsyncThunk(
  'securedApi/singleDevice/deletePublicLink',
  async ({ deviceId }, thunkAPI) => {
    try {
      const response = await RaaAuthenticatedRemoteService.globalInstance()
        .delete(`${envConfig.apiUrl}/api/v1/device-data/public-link/${deviceId}`);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message,
      });
    }
  },
);

const fetchDeviceAlertChannels = createAsyncThunk(
  'securedApi/singleDevice/fetchAlertChannels',
  async ({ deviceId }, thunkAPI) => {
    try {
      const response = await RaaAuthenticatedRemoteService.globalInstance().get(
        `${envConfig.apiUrl}/api/v1/alerts/channel-list/${deviceId}`,
      );
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message,
      });
    }
  },
);

const updateDeviceLocation = createAsyncThunk(
  'securedApi/singleDevice/updateLocation',
  async (payload, thunkAPI) => {
    try {
      const {
        deviceId, location, lat, lng,
      } = payload;
      const formData = new FormData();
      formData.append('id', deviceId);
      // Add either location or coordinates, but not both
      if (location !== undefined) {
        formData.append('location', location);
      } else if (lat !== undefined && lng !== undefined) {
        formData.append('lat', lat);
        formData.append('lng', lng);
      } else {
        return thunkAPI.rejectWithValue({
          message: 'Either location or coordinates (lat/lng) must be provided',
        });
      }

      const response = await RaaAuthenticatedRemoteService.globalInstance()
        .post(`${envConfig.raaUrl}/devices/data/change-device-location`, { data: formData });
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message,
      });
    }
  },
);

const toggleChannelGroupVisibility = createAsyncThunk(
  'securedApi/singleDevice/toggleChannelGroupVisibility',
  async (payload, thunkAPI) => {
    try {
      return await RaaAuthenticatedRemoteService.globalInstance().patch(
        `${envConfig.apiUrl}/api/v1/device-data/update-sensor/`,
        {
          data: {
            channelKey: payload.channelKey,
            isHidden: payload.isHidden,
            label: payload.label,
            channels: [],
          },
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );
    } catch (error) {
      return thunkAPI.rejectWithValue({
        message: error.message,
      });
    }
  },
);

// Initial state
const initialState = {
  initialLoad: true,
  device: null,
  crudOperation: CrudType.READ,
  crudStatus: CrudStatus.IDLE,
  errors: [],
  deviceAssignment: null,
  assignmentStatus: CrudStatus.IDLE,
  alertChannels: null,
  alertChannelsStatus: CrudStatus.IDLE,
  publicLinkStatus: CrudStatus.IDLE,
  publicPashHash: null,
  sensorUpdateStatus: CrudStatus.IDLE,
  hiddenChannelGroups: [],
  error: false,
};

// Slice
const singleDeviceSlice = createSlice({
  name: 'singleDevice',
  initialState,
  reducers: {
    clearSingleDeviceState: () => initialState,
    resetDeviceDataFormState: (state) => {
      state.sensorUpdateStatus = CrudStatus.IDLE;
      state.error = false;
      state.validationErrors = [];
    },
  },
  extraReducers: (builder) => {
    builder
    // Fetch Single Device
      .addCase(fetchSingleDevice.pending, (state) => {
        state.crudStatus = CrudStatus.PENDING;
        state.crudOperation = CrudType.READ;
        state.errors = [];
      })
      .addCase(fetchSingleDevice.fulfilled, (state, action) => {
        state.device = action.payload;
        state.crudStatus = CrudStatus.SUCCESS;
        state.initialLoad = false;

        const hiddenGroups = [];
        if (action.payload.sensors) {
          action.payload.sensors.forEach((sensor) => {
            if (sensor.channelGroups) {
              sensor.channelGroups.forEach((group) => {
                if (group.isHidden) {
                  hiddenGroups.push(group.channelKey);
                }
              });
            }
          });
        }
        state.hiddenChannelGroups = hiddenGroups;
      })
      .addCase(fetchSingleDevice.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.errors = action.payload;
        state.initialLoad = false;
      })

    // Update Device Name
      .addCase(updateDeviceName.pending, (state) => {
        state.crudStatus = CrudStatus.PENDING;
        state.crudOperation = CrudType.UPDATE;
        state.errors = [];
      })
      .addCase(updateDeviceName.fulfilled, (state) => {
        // state.device = action.payload;
        state.crudStatus = CrudStatus.SUCCESS;
      })
      .addCase(updateDeviceName.rejected, (state, action) => {
        state.crudStatus = CrudStatus.ERROR;
        state.errors = action.payload;
      })
    // Create Public Link
      .addCase(createDevicePublicLink.pending, (state) => {
        state.publicLinkStatus = CrudStatus.PENDING;
        state.errors = [];
      })
      .addCase(createDevicePublicLink.fulfilled, (state, action) => {
        state.publicLinkStatus = CrudStatus.SUCCESS;
        state.publicLinkUrl = action.payload;
        // if (state.device) {
        //   state.device.publicLinkHash = action.payload.split('/').pop();
        // }
      })
      .addCase(createDevicePublicLink.rejected, (state, action) => {
        state.publicLinkStatus = CrudStatus.ERROR;
        state.errors = action.payload;
      })
    // Delete Device Public Link
      .addCase(deleteDevicePublicLink.pending, (state) => {
        state.publicLinkStatus = CrudStatus.PENDING;
        state.errors = [];
      })
      .addCase(deleteDevicePublicLink.fulfilled, (state) => {
        state.publicLinkStatus = CrudStatus.SUCCESS;
        // state.publicPageHash = '';
        // if (state.device) {
        //   state.device.publicLinkHash = null;
        // }
      })
      .addCase(deleteDevicePublicLink.rejected, (state, action) => {
        state.publicLinkStatus = CrudStatus.ERROR;
        state.errors = action.payload;
      })
      .addCase(updateSensor.pending, (state) => {
        state.sensorUpdateStatus = CrudStatus.PENDING;
        state.errors = [];
      })
      .addCase(updateSensor.fulfilled, (state) => {
        state.sensorUpdateStatus = CrudStatus.SUCCESS;
        // Update the device sensors if needed
        // if (state.device && action.payload) {
        // Assuming the payload contains updated sensor data
        // eslint-disable-next-line max-len
        // const updatedSensors = state.device.sensors.map((sensor) => (sensor.id === action.payload.id ? action.payload : sensor));
        // state.device = {
        //   ...state.device,
        //   sensors: updatedSensors,
        // };
        // }
      })
      .addCase(updateSensor.rejected, (state, action) => {
        state.sensorUpdateStatus = CrudStatus.ERROR;
        state.errors = action.payload;
      })
    // Update Device Location
      .addCase(updateDeviceLocation.pending, (state) => {
        state.locationUpdateStatus = CrudStatus.PENDING;
        state.errors = [];
      })
      .addCase(updateDeviceLocation.fulfilled, (state) => {
        state.locationUpdateStatus = CrudStatus.SUCCESS;
        // if (state.device) {
        //   state.device.location = action.payload.location;
        //   state.device.geocode = {
        //     latitude: action.payload.lat,
        //     longitude: action.payload.lng,
        //   };
        // }
      })
      .addCase(updateDeviceLocation.rejected, (state, action) => {
        state.locationUpdateStatus = CrudStatus.ERROR;
        state.errors = action.payload;
      })
      .addCase(fetchDeviceAlertChannels.pending, (state) => {
        state.alertChannelsStatus = CrudStatus.PENDING;
        state.errors = [];
      })
      .addCase(fetchDeviceAlertChannels.fulfilled, (state, action) => {
        state.alertChannels = action.payload;
        state.alertChannelsStatus = CrudStatus.SUCCESS;
      })
      .addCase(fetchDeviceAlertChannels.rejected, (state, action) => {
        state.alertChannelsStatus = CrudStatus.ERROR;
        state.errors = action.payload;
        state.alertChannels = null;
      })
      .addCase(toggleChannelGroupVisibility.pending, (state) => {
        state.sensorUpdateStatus = CrudStatus.PENDING;
        state.errors = [];
      })
      .addCase(toggleChannelGroupVisibility.fulfilled, (state) => {
        state.sensorUpdateStatus = CrudStatus.SUCCESS;
        // Update hiddenChannelGroups array
        // console.log('action ', action.payload);
        // if (action.payload.isHidden) {
        //   if (!state.hiddenChannelGroups.includes(action.payload.channelKey)) {
        //     state.hiddenChannelGroups.push(action.payload.channelKey);
        //   }
        // } else {
        //   state.hiddenChannelGroups = state.hiddenChannelGroups.filter(
        //     (key) => key !== action.payload.channelKey,
        //   );
        // }
      })
      .addCase(toggleChannelGroupVisibility.rejected, (state, action) => {
        state.sensorUpdateStatus = CrudStatus.ERROR;
        state.errors = action.payload;
      });
  },
});

const { actions, reducer } = singleDeviceSlice;

export const { clearSingleDeviceState, resetDeviceDataFormState } = actions;

export {
  fetchSingleDevice,
  updateDeviceName,
  // togglePublicLink,
  createDevicePublicLink,
  deleteDevicePublicLink,
  updateSensor,
  updateDeviceLocation,
  toggleChannelGroupVisibility,
  selectSingleDevice,
  selectSingleDeviceCrudOperation,
  selectSingleDeviceCrudStatus,
  selectSingleDeviceErrors,
  selectSingleDeviceInitialLoading,
  selectDeviceAlertChannels,
  selectAlertChannelsStatus,
  selectPublicLinkStatus,
  selectDevicePublicPageHash,
  selectSensorUpdateStatus,
  selectLocationUpdateStatus,
  selectHiddenChannelGroups,
};

export default reducer;
